import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  DollarSign,
  BarChart3,
  Settings,
  FileText,
  Calendar,
  Briefcase,
  TrendingUp,
  FlaskConical,
  LogOut,
  ArrowLeft,
  Edit,
  MoreVertical,
} from 'lucide-react';
import { DataModeSelector } from '@/components/ui/data-mode-selector';
import { DemoModeHeaderControls } from '@/components/demo-mode-header-controls';
import * as DefaultSidebar from '@/components/ui/sidebar';
import * as EvoSidebar from '@/components/blocks/evo-sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import { useDemoSession } from '@/providers/DemoSessionProvider';
import { useKeyboardShortcuts, type KeyboardShortcut } from '@/hooks/use-keyboard-shortcuts';
import { CommandPalette } from '@/components/command-palette';
import { KeyboardShortcutsDialog } from '@/components/keyboard-shortcuts-dialog';
import { MinimizedFormIndicator } from '@/components/minimized-form-indicator';
import { useProjectForm } from '@/providers/ProjectFormProvider';
import { shouldShowSensitiveDataToggle } from '@/config/sensitive-data-pages';
import { getHealthBadgeColor, formatStatus } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/routes';

interface AppLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  projectContext?: {
    projectName: string;
    clientName: string;
    projectManager: string;
    startDate: string;
    endDate: string;
    health: 'green' | 'amber' | 'red';
    status: string;
    onBack: () => void;
    onEdit?: () => void;
    onMoreOptions?: () => void;
  };
}

const navigation = [
  { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard, section: 'main', route: ROUTES.DASHBOARD },
  { id: 'projects', name: 'Projects', icon: FolderKanban, section: 'main', route: ROUTES.PROJECTS },
  { id: 'portfolio', name: 'Portfolio', icon: Briefcase, section: 'main', route: ROUTES.PORTFOLIO },
  
  { id: 'utilisation', name: 'Utilisation', icon: TrendingUp, section: 'financial', route: ROUTES.UTILISATION },
  { id: 'wip-reporting', name: 'WIP Reporting', icon: FileText, section: 'financial', route: ROUTES.WIP_REPORTING },
  { id: 'profitability', name: 'Profitability', icon: DollarSign, section: 'financial', route: ROUTES.PROFITABILITY },
  { id: 'budgets', name: 'Budgets & Variance', icon: BarChart3, section: 'financial', route: ROUTES.BUDGETS },
  
  { id: 'resources', name: 'Resource Allocation', icon: Users, section: 'planning', route: ROUTES.RESOURCES },
  { id: 'gantt', name: 'Gantt Charts', icon: Calendar, section: 'planning', route: ROUTES.GANTT },
  
  { id: 'feature-labs', name: 'Feature Labs', icon: FlaskConical, section: 'settings', route: ROUTES.FEATURE_LABS },
  { id: 'settings', name: 'Settings', icon: Settings, section: 'settings', route: ROUTES.SETTINGS },
];

export function AppLayout({ children, currentPage, projectContext }: AppLayoutProps) {
  const { preferences } = useUserPreferences();
  const { userName, userInitials, logout } = useDemoSession();
  const { restoreForm } = useProjectForm();
  const navigate = useNavigate();
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  
  // Select sidebar implementation based on user preference
  const isEvo = preferences.sidebarStyle === 'evo';
  const S = isEvo ? EvoSidebar : DefaultSidebar;
  
  const sections = {
    main: navigation.filter(item => item.section === 'main'),
    financial: navigation.filter(item => item.section === 'financial'),
    planning: navigation.filter(item => item.section === 'planning'),
    settings: navigation.filter(item => item.section === 'settings'),
  };

  // Define keyboard shortcuts
  const shortcuts: KeyboardShortcut[] = [
    // Navigation shortcuts (Cmd/Ctrl + 1-9)
    {
      key: '1',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.DASHBOARD),
      description: 'Go to Dashboard',
      category: 'navigation',
    },
    {
      key: '2',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.PROJECTS),
      description: 'Go to Projects',
      category: 'navigation',
    },
    {
      key: '3',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.PORTFOLIO),
      description: 'Go to Portfolio',
      category: 'navigation',
    },
    {
      key: '4',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.UTILISATION),
      description: 'Go to Utilisation',
      category: 'navigation',
    },
    {
      key: '5',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.WIP_REPORTING),
      description: 'Go to WIP Reporting',
      category: 'navigation',
    },
    {
      key: '6',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.PROFITABILITY),
      description: 'Go to Profitability',
      category: 'navigation',
    },
    {
      key: '7',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.BUDGETS),
      description: 'Go to Budgets & Variance',
      category: 'navigation',
    },
    {
      key: '8',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.RESOURCES),
      description: 'Go to Resource Allocation',
      category: 'navigation',
    },
    {
      key: '9',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.GANTT),
      description: 'Go to Gantt Charts',
      category: 'navigation',
    },
    // Command palette (Cmd/Ctrl + K)
    {
      key: 'k',
      ctrlKey: true,
      metaKey: true,
      action: () => setCommandPaletteOpen(true),
      description: 'Open command palette',
      category: 'actions',
    },
    // Settings (Cmd/Ctrl + ,)
    {
      key: ',',
      ctrlKey: true,
      metaKey: true,
      action: () => navigate(ROUTES.SETTINGS),
      description: 'Open Settings',
      category: 'actions',
    },
    // Keyboard shortcuts help (Cmd/Ctrl + /)
    {
      key: '/',
      ctrlKey: true,
      metaKey: true,
      action: () => setShortcutsDialogOpen(true),
      description: 'Show keyboard shortcuts',
      category: 'actions',
    },
  ];

  // Register keyboard shortcuts
  useKeyboardShortcuts({ shortcuts });

  // Prepare shortcuts for the help dialog
  const shortcutCategories = [
    {
      title: 'Navigation',
      shortcuts: [
        { key: '1', ctrlKey: true, metaKey: true, description: 'Go to Dashboard' },
        { key: '2', ctrlKey: true, metaKey: true, description: 'Go to Projects' },
        { key: '3', ctrlKey: true, metaKey: true, description: 'Go to Portfolio' },
        { key: '4', ctrlKey: true, metaKey: true, description: 'Go to Utilisation' },
        { key: '5', ctrlKey: true, metaKey: true, description: 'Go to WIP Reporting' },
        { key: '6', ctrlKey: true, metaKey: true, description: 'Go to Profitability' },
        { key: '7', ctrlKey: true, metaKey: true, description: 'Go to Budgets & Variance' },
        { key: '8', ctrlKey: true, metaKey: true, description: 'Go to Resource Allocation' },
        { key: '9', ctrlKey: true, metaKey: true, description: 'Go to Gantt Charts' },
      ],
    },
    {
      title: 'Actions',
      shortcuts: [
        { key: 'k', ctrlKey: true, metaKey: true, description: 'Open command palette' },
        { key: ',', ctrlKey: true, metaKey: true, description: 'Open Settings' },
        { key: '/', ctrlKey: true, metaKey: true, description: 'Show keyboard shortcuts' },
      ],
    },
    {
      title: 'User Interface',
      shortcuts: [
        { key: 'b', ctrlKey: true, metaKey: true, description: 'Toggle sidebar' },
        { key: 'Escape', description: 'Close dialogs and modals' },
      ],
    },
  ];

  return (
    <div className="flex h-screen flex-col bg-background">
      {/* App Runner-Bar - Fixed Top Bar */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-6 z-50">
        <div className="flex items-center gap-3">
          <img 
            src="/assets/AppIcon.svg" 
            alt="Access Projects" 
            className="h-8 w-8"
          />
          <div>
            <h1 className="text-base font-semibold leading-tight">Access Projects</h1>
            <p className="text-xs text-muted-foreground leading-tight">Project Management</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Demo/Live Mode Toggle */}
          <DataModeSelector />
          
          {/* Theme Toggle Button - Hidden for Evo workspace embedding */}
          {/* Theme toggle disabled - app uses light mode by default and listens for Evo workspace theme changes */}
          {/* To re-enable manual theme switching, uncomment the line below: */}
          {/* <ThemeToggle /> */}
          
          {/* User Profile Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs font-semibold">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">{userName}</span>
                  <span className="text-xs text-muted-foreground">Senior PM</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate(ROUTES.USER_PREFERENCES)}>
                User Preferences
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShortcutsDialogOpen(true)}>
                Keyboard Shortcuts
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="gap-2">
                <LogOut className="h-4 w-4" />
                <span>Log Out</span>
                <Badge variant="outline" className="ml-auto text-[10px] px-1.5 py-0">
                  DEMO
                </Badge>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Sidebar + Content Area */}
      <div className="flex flex-1 min-h-0">
        <S.SidebarProvider defaultOpen={true} side={preferences.sidebarPosition}>
          {/* Reorder sidebar and content based on position preference */}
          {preferences.sidebarPosition === 'left' ? (
            <>
              {/* Sidebar on left */}
              <S.Sidebar collapsible="icon" side="left" className="border-r">
                <S.SidebarContent>
                  {/* Main Section */}
                  <S.SidebarGroup>
                    <div className="flex items-center justify-between w-full">
                      <S.SidebarGroupLabel className="flex-1">MAIN</S.SidebarGroupLabel>
                      <S.SidebarTrigger className="shrink-0" />
                    </div>
                    <S.SidebarGroupContent>
                      <S.SidebarMenu>
                        {sections.main.map((item) => (
                          <S.SidebarMenuItem key={item.id}>
                            <S.SidebarMenuButton
                              onClick={() => navigate(item.route)}
                              isActive={currentPage === item.id}
                              tooltip={item.name}
                            >
                              <item.icon />
                              <span>{item.name}</span>
                            </S.SidebarMenuButton>
                          </S.SidebarMenuItem>
                        ))}
                      </S.SidebarMenu>
                    </S.SidebarGroupContent>
                  </S.SidebarGroup>

                  <S.SidebarSeparator />

                  {/* Financial Section */}
                  <S.SidebarGroup>
                    <S.SidebarGroupLabel>FINANCIAL</S.SidebarGroupLabel>
                    <S.SidebarGroupContent>
                      <S.SidebarMenu>
                        {sections.financial.map((item) => (
                          <S.SidebarMenuItem key={item.id}>
                            <S.SidebarMenuButton
                              onClick={() => navigate(item.route)}
                              isActive={currentPage === item.id}
                              tooltip={item.name}
                            >
                              <item.icon />
                              <span>{item.name}</span>
                            </S.SidebarMenuButton>
                          </S.SidebarMenuItem>
                        ))}
                      </S.SidebarMenu>
                    </S.SidebarGroupContent>
                  </S.SidebarGroup>

                  <S.SidebarSeparator />

                  {/* Planning Section */}
                  <S.SidebarGroup>
                    <S.SidebarGroupLabel>PLANNING</S.SidebarGroupLabel>
                    <S.SidebarGroupContent>
                      <S.SidebarMenu>
                        {sections.planning.map((item) => (
                          <S.SidebarMenuItem key={item.id}>
                            <S.SidebarMenuButton
                              onClick={() => navigate(item.route)}
                              isActive={currentPage === item.id}
                              tooltip={item.name}
                            >
                              <item.icon />
                              <span>{item.name}</span>
                            </S.SidebarMenuButton>
                          </S.SidebarMenuItem>
                        ))}
                      </S.SidebarMenu>
                    </S.SidebarGroupContent>
                  </S.SidebarGroup>

                  <S.SidebarSeparator />

                  {/* Settings Section */}
                  <S.SidebarGroup>
                    <S.SidebarGroupContent>
                      <S.SidebarMenu>
                        {sections.settings.map((item) => (
                          <S.SidebarMenuItem key={item.id}>
                            <S.SidebarMenuButton
                              onClick={() => navigate(item.route)}
                              isActive={currentPage === item.id}
                              tooltip={item.name}
                              className={item.id === 'feature-labs' ? 'text-blue-600 dark:text-blue-400' : ''}
                            >
                              <item.icon />
                              <span>{item.name}</span>
                            </S.SidebarMenuButton>
                          </S.SidebarMenuItem>
                        ))}
                      </S.SidebarMenu>
                    </S.SidebarGroupContent>
                  </S.SidebarGroup>
                </S.SidebarContent>

                <S.SidebarFooter>
                  <MinimizedFormIndicator 
                    onClick={() => {
                      // Navigate to projects page first, then restore form
                      navigate(ROUTES.PROJECTS);
                      restoreForm();
                    }}
                  />
                </S.SidebarFooter>
              </S.Sidebar>

              {/* Content area */}
              {isEvo ? (
                <div className="bg-background relative flex w-full flex-1 flex-col">
                  <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-4">
                    {projectContext ? (
                      <>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={projectContext.onBack}
                            className="shrink-0"
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </Button>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h2 className="text-base font-semibold truncate">
                                {projectContext.projectName}
                              </h2>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground truncate">
                                {projectContext.clientName}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              PM: {projectContext.projectManager} • {projectContext.startDate} - {projectContext.endDate}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={getHealthBadgeColor(projectContext.health)}>
                            {projectContext.health.toUpperCase()}
                          </Badge>
                          <Badge variant="outline">
                            {formatStatus(projectContext.status)}
                          </Badge>
                          {projectContext.onEdit && (
                            <Button variant="outline" size="sm" onClick={projectContext.onEdit}>
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                          {projectContext.onMoreOptions && (
                            <Button variant="ghost" size="sm" onClick={projectContext.onMoreOptions}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          )}
                          <DemoModeHeaderControls showSensitiveToggle={shouldShowSensitiveDataToggle(currentPage)} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div>
                            <h2 className="text-base font-semibold">
                              {navigation.find(item => item.id === currentPage)?.name || currentPage.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </h2>
                            <p className="text-xs text-muted-foreground">
                              Access PSA Suite • UK Professional Services
                            </p>
                          </div>
                        </div>
                        <DemoModeHeaderControls showSensitiveToggle={shouldShowSensitiveDataToggle(currentPage)} />
                      </>
                    )}
                  </header>
                  <main className="flex-1 overflow-auto">
                    <div className="container mx-auto p-6">
                      {children}
                    </div>
                  </main>
                </div>
              ) : (
                <DefaultSidebar.SidebarInset>
                  <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-4">
                    {projectContext ? (
                      <>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={projectContext.onBack}
                            className="shrink-0"
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </Button>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h2 className="text-base font-semibold truncate">
                                {projectContext.projectName}
                              </h2>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground truncate">
                                {projectContext.clientName}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              PM: {projectContext.projectManager} • {projectContext.startDate} - {projectContext.endDate}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={getHealthBadgeColor(projectContext.health)}>
                            {projectContext.health.toUpperCase()}
                          </Badge>
                          <Badge variant="outline">
                            {formatStatus(projectContext.status)}
                          </Badge>
                          {projectContext.onEdit && (
                            <Button variant="outline" size="sm" onClick={projectContext.onEdit}>
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                          {projectContext.onMoreOptions && (
                            <Button variant="ghost" size="sm" onClick={projectContext.onMoreOptions}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          )}
                          <DemoModeHeaderControls showSensitiveToggle={shouldShowSensitiveDataToggle(currentPage)} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div>
                            <h2 className="text-base font-semibold">
                              {navigation.find(item => item.id === currentPage)?.name || currentPage.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </h2>
                            <p className="text-xs text-muted-foreground">
                              Access PSA Suite • UK Professional Services
                            </p>
                          </div>
                        </div>
                        <DemoModeHeaderControls showSensitiveToggle={shouldShowSensitiveDataToggle(currentPage)} />
                      </>
                    )}
                  </header>
                  <main className="flex-1 overflow-auto">
                    <div className="container mx-auto p-6">
                      {children}
                    </div>
                  </main>
                </DefaultSidebar.SidebarInset>
              )}
            </>
          ) : (
            <>
              {/* Content first, then sidebar on right */}
              {isEvo ? (
                <div className="bg-background relative flex w-full flex-1 flex-col">
                  <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-4">
                    {projectContext ? (
                      <>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={projectContext.onBack}
                            className="shrink-0"
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </Button>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h2 className="text-base font-semibold truncate">
                                {projectContext.projectName}
                              </h2>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground truncate">
                                {projectContext.clientName}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              PM: {projectContext.projectManager} • {projectContext.startDate} - {projectContext.endDate}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={getHealthBadgeColor(projectContext.health)}>
                            {projectContext.health.toUpperCase()}
                          </Badge>
                          <Badge variant="outline">
                            {formatStatus(projectContext.status)}
                          </Badge>
                          {projectContext.onEdit && (
                            <Button variant="outline" size="sm" onClick={projectContext.onEdit}>
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                          {projectContext.onMoreOptions && (
                            <Button variant="ghost" size="sm" onClick={projectContext.onMoreOptions}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          )}
                          <DemoModeHeaderControls showSensitiveToggle={shouldShowSensitiveDataToggle(currentPage)} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div>
                            <h2 className="text-base font-semibold">
                              {navigation.find(item => item.id === currentPage)?.name || currentPage.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </h2>
                            <p className="text-xs text-muted-foreground">
                              Access PSA Suite • UK Professional Services
                            </p>
                          </div>
                        </div>
                        <DemoModeHeaderControls showSensitiveToggle={shouldShowSensitiveDataToggle(currentPage)} />
                      </>
                    )}
                  </header>
                  <main className="flex-1 overflow-auto">
                    <div className="container mx-auto p-6">
                      {children}
                    </div>
                  </main>
                </div>
              ) : (
                <DefaultSidebar.SidebarInset>
                  <header className="flex h-14 shrink-0 items-center justify-between border-b bg-card px-4">
                    {projectContext ? (
                      <>
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={projectContext.onBack}
                            className="shrink-0"
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </Button>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h2 className="text-base font-semibold truncate">
                                {projectContext.projectName}
                              </h2>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground truncate">
                                {projectContext.clientName}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              PM: {projectContext.projectManager} • {projectContext.startDate} - {projectContext.endDate}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge className={getHealthBadgeColor(projectContext.health)}>
                            {projectContext.health.toUpperCase()}
                          </Badge>
                          <Badge variant="outline">
                            {formatStatus(projectContext.status)}
                          </Badge>
                          {projectContext.onEdit && (
                            <Button variant="outline" size="sm" onClick={projectContext.onEdit}>
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                          {projectContext.onMoreOptions && (
                            <Button variant="ghost" size="sm" onClick={projectContext.onMoreOptions}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          )}
                          <DemoModeHeaderControls showSensitiveToggle={shouldShowSensitiveDataToggle(currentPage)} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div>
                            <h2 className="text-base font-semibold">
                              {navigation.find(item => item.id === currentPage)?.name || currentPage.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </h2>
                            <p className="text-xs text-muted-foreground">
                              Access PSA Suite • UK Professional Services
                            </p>
                          </div>
                        </div>
                        <DemoModeHeaderControls showSensitiveToggle={shouldShowSensitiveDataToggle(currentPage)} />
                      </>
                    )}
                  </header>
                  <main className="flex-1 overflow-auto">
                    <div className="container mx-auto p-6">
                      {children}
                    </div>
                  </main>
                </DefaultSidebar.SidebarInset>
              )}

              {/* Sidebar on right */}
              <S.Sidebar collapsible="icon" side="right" className="border-l">
                <S.SidebarContent>
                  {/* Main Section */}
                  <S.SidebarGroup>
                    <div className="flex items-center justify-between w-full">
                      <S.SidebarTrigger className="shrink-0" />
                      <S.SidebarGroupLabel className="flex-1">MAIN</S.SidebarGroupLabel>
                    </div>
                    <S.SidebarGroupContent>
                      <S.SidebarMenu>
                        {sections.main.map((item) => (
                          <S.SidebarMenuItem key={item.id}>
                            <S.SidebarMenuButton
                              onClick={() => navigate(item.route)}
                              isActive={currentPage === item.id}
                              tooltip={item.name}
                            >
                              <item.icon />
                              <span>{item.name}</span>
                            </S.SidebarMenuButton>
                          </S.SidebarMenuItem>
                        ))}
                      </S.SidebarMenu>
                    </S.SidebarGroupContent>
                  </S.SidebarGroup>

                  <S.SidebarSeparator />

                  {/* Financial Section */}
                  <S.SidebarGroup>
                    <S.SidebarGroupLabel>FINANCIAL</S.SidebarGroupLabel>
                    <S.SidebarGroupContent>
                      <S.SidebarMenu>
                        {sections.financial.map((item) => (
                          <S.SidebarMenuItem key={item.id}>
                            <S.SidebarMenuButton
                              onClick={() => navigate(item.route)}
                              isActive={currentPage === item.id}
                              tooltip={item.name}
                            >
                              <item.icon />
                              <span>{item.name}</span>
                            </S.SidebarMenuButton>
                          </S.SidebarMenuItem>
                        ))}
                      </S.SidebarMenu>
                    </S.SidebarGroupContent>
                  </S.SidebarGroup>

                  <S.SidebarSeparator />

                  {/* Planning Section */}
                  <S.SidebarGroup>
                    <S.SidebarGroupLabel>PLANNING</S.SidebarGroupLabel>
                    <S.SidebarGroupContent>
                      <S.SidebarMenu>
                        {sections.planning.map((item) => (
                          <S.SidebarMenuItem key={item.id}>
                            <S.SidebarMenuButton
                              onClick={() => navigate(item.route)}
                              isActive={currentPage === item.id}
                              tooltip={item.name}
                            >
                              <item.icon />
                              <span>{item.name}</span>
                            </S.SidebarMenuButton>
                          </S.SidebarMenuItem>
                        ))}
                      </S.SidebarMenu>
                    </S.SidebarGroupContent>
                  </S.SidebarGroup>

                  <S.SidebarSeparator />

                  {/* Settings Section */}
                  <S.SidebarGroup>
                    <S.SidebarGroupContent>
                      <S.SidebarMenu>
                        {sections.settings.map((item) => (
                          <S.SidebarMenuItem key={item.id}>
                            <S.SidebarMenuButton
                              onClick={() => navigate(item.route)}
                              isActive={currentPage === item.id}
                              tooltip={item.name}
                              className={item.id === 'feature-labs' ? 'text-blue-600 dark:text-blue-400' : ''}
                            >
                              <item.icon />
                              <span>{item.name}</span>
                            </S.SidebarMenuButton>
                          </S.SidebarMenuItem>
                        ))}
                      </S.SidebarMenu>
                    </S.SidebarGroupContent>
                  </S.SidebarGroup>
                </S.SidebarContent>

                <S.SidebarFooter>
                  <MinimizedFormIndicator 
                    onClick={() => {
                      // Navigate to projects page first, then restore form
                      navigate(ROUTES.PROJECTS);
                      restoreForm();
                    }}
                  />
                </S.SidebarFooter>
              </S.Sidebar>
            </>
          )}
        </S.SidebarProvider>
      </div>

      {/* Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onNavigate={(page) => navigate(`/${page}`)}
      />

      {/* Keyboard Shortcuts Dialog */}
      <KeyboardShortcutsDialog
        open={shortcutsDialogOpen}
        onOpenChange={setShortcutsDialogOpen}
        categories={shortcutCategories}
      />
    </div>
  );
}
